import React, { useCallback } from "react";
import { LoadingIndicator, ErrorMessage } from "@jsq/ds/core";
import { FormTextInput } from "@jsq/ds/forms";
import { CountrySelectionDropDown } from "../../../../common/components/CountrySelectionDropDown";
import { SimpleFormRow } from "../../../../common/components/Form/Layout/SimpleFormRow";
import { PaymentMethod } from "../../../../common/enums";
import { isUSA } from "../../../../common/utils";
import { useGetCountryCodesQuery } from "../../../../graphql/supergraph.index";
import { SharedAddressInput } from "../../../SharedAddressInput";
import { CountrySelectProps } from "../../../SharedAddressInput/types";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { useGetDetailedPaymentPref } from "../../hooks/use-get-detailed-payment-pref";
import { getCountryValue, getNameFromCountry } from "../../mappers";
import { PaymentPrefLayoutForm } from "../PaymentPrefLayoutForm";
import { type ChildComponentProps } from "../types";
import {
  CheckPaymentPrefInputFields,
  CheckPaymentPrefInputSchema,
  type CheckPaymentPrefInputType,
} from "./constants";
import { mapContextStateToFormState, mapFormStateToContextState } from "./utils";

export const CheckAddressCountrySelect: React.FC<CountrySelectProps> = ({
  name,
  className,
  isFull,
  placeholder,
  handleChange,
  label,
}) => {
  return (
    <CountrySelectionDropDown
      name={name}
      className={className}
      isFull={isFull}
      placeholder={placeholder}
      handleChange={handleChange}
      getValue={getCountryValue}
      label={label}
    />
  );
};

export const CheckPaymentPrefInput: React.FC<ChildComponentProps> = ({
  onContinueClick,
  onBackClick,
  onBackButtonName,
}) => {
  const { setPaymentPrefDetail, workflowMode } = useConsolidatedPaymentPrefContext();
  const { data: paymentPrefDetail, loading, error } = useGetDetailedPaymentPref();

  const handleFormSubmission = useCallback(
    (values: CheckPaymentPrefInputType) => {
      setPaymentPrefDetail(mapFormStateToContextState(values));
      onContinueClick();
    },
    [setPaymentPrefDetail, onContinueClick],
  );

  /**
   * Pre-loading the country codes to reduce jitter of country fields appearing
   */
  const { loading: countryLoading } = useGetCountryCodesQuery();

  if (loading || countryLoading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <ErrorMessage>Failed to load payment preference</ErrorMessage>;
  }

  return (
    <PaymentPrefLayoutForm
      paymentMethod={PaymentMethod.Check}
      onSubmit={handleFormSubmission}
      initialValues={mapContextStateToFormState(
        paymentPrefDetail?.__typename === "CheckPaymentPrefObjectType"
          ? paymentPrefDetail
          : undefined,
      )}
      validationSchema={CheckPaymentPrefInputSchema}
      onBackClick={onBackClick}
      onBackButtonName={onBackButtonName}
      workflowMode={workflowMode}
    >
      <SimpleFormRow>
        <FormTextInput
          label="Recipient name"
          isFullWidth
          name={CheckPaymentPrefInputFields.PayeeName}
        />
      </SimpleFormRow>
      <SimpleFormRow>
        <FormTextInput
          label="Memo"
          isFullWidth
          name={CheckPaymentPrefInputFields.Memo}
          helpText="Optional"
        />
      </SimpleFormRow>
      <SharedAddressInput
        title="Address"
        sameAsMailingAddressLabel="Same as investor account mailing address"
        CountrySelect={CheckAddressCountrySelect}
        includeSameAsMailingAddressField
        name={CheckPaymentPrefInputFields.Address}
        testIsUSA={(country) => isUSA(getNameFromCountry(country))}
      />
    </PaymentPrefLayoutForm>
  );
};
